import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CreateDeploymentApplicationErrors,
  CreateDeploymentOptions,
  Deployment,
  DeploymentsQueryBuilder,
  GetDeploymentPipelineDescriptionApplicationErrors,
  GetDeploymentPipelineDescriptionResponse,
  ListDeploymentsResponse,
  PublishDeploymentApplicationErrors,
  PublishDeploymentOptions,
  PublishDeploymentResponse,
  PublishRCDeploymentResponse,
  PublishRcDeploymentApplicationErrors,
  PublishRcDeploymentOptions,
  ReportDeploymentPipelineResultApplicationErrors,
  ReportDeploymentPipelineResultOptions,
  UpdateExtendedFieldsOptions,
  UpdateExtendedFieldsResponse,
  createDeployment as universalCreateDeployment,
  getDeployment as universalGetDeployment,
  getDeploymentPipelineDescription as universalGetDeploymentPipelineDescription,
  listDeployments as universalListDeployments,
  publishDeployment as universalPublishDeployment,
  publishRcDeployment as universalPublishRcDeployment,
  queryDeployments as universalQueryDeployments,
  reportDeploymentPipelineResult as universalReportDeploymentPipelineResult,
  updateExtendedFields as universalUpdateExtendedFields,
} from './document-management-editor-deployments-v1-deployment-deployments.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/editor-deployments' };

export function createDeployment(
  httpClient: HttpClient
): CreateDeploymentSignature {
  return (options?: CreateDeploymentOptions) =>
    universalCreateDeployment(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateDeploymentSignature {
  /**
   * Creates a new Deployment
   * @returns The created Deployment
   */
  (options?: CreateDeploymentOptions): Promise<
    NonNullablePaths<
      Deployment,
      | `editorRevision.siteRevision`
      | `rcPublish.rcLabel`
      | `rcPublish.useUniqueLabel`
      | `_id`
      | `deploymentType`
      | `pipelines`
      | `pipelines.${number}.deploymentPipelineId`
      | `pipelines.${number}.pipelineStatus`
      | `deploymentPipelinesStatus`,
      4
    > & {
      __applicationErrorsType?: CreateDeploymentApplicationErrors;
    }
  >;
}

export function getDeployment(httpClient: HttpClient): GetDeploymentSignature {
  return (deploymentId: string) =>
    universalGetDeployment(
      deploymentId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetDeploymentSignature {
  /**
   * Get a Deployment by id
   * @param - Id of the Deployment to retrieve
   * @returns The retrieved Deployment
   */
  (deploymentId: string): Promise<
    NonNullablePaths<
      Deployment,
      | `editorRevision.siteRevision`
      | `rcPublish.rcLabel`
      | `rcPublish.useUniqueLabel`
      | `_id`
      | `deploymentType`
      | `pipelines`
      | `pipelines.${number}.deploymentPipelineId`
      | `pipelines.${number}.pipelineStatus`
      | `deploymentPipelinesStatus`,
      4
    >
  >;
}

export function publishDeployment(
  httpClient: HttpClient
): PublishDeploymentSignature {
  return (deploymentId: string, options?: PublishDeploymentOptions) =>
    universalPublishDeployment(
      deploymentId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface PublishDeploymentSignature {
  /**
   * Publish a Deployment
   * @param - Id of the Deployment to publish
   */
  (deploymentId: string, options?: PublishDeploymentOptions): Promise<
    NonNullablePaths<
      PublishDeploymentResponse,
      | `deployment.editorRevision.siteRevision`
      | `deployment.rcPublish.rcLabel`
      | `deployment.rcPublish.useUniqueLabel`
      | `deployment._id`
      | `deployment.deploymentType`
      | `deployment.pipelines`
      | `deployment.pipelines.${number}.deploymentPipelineId`
      | `deployment.pipelines.${number}.pipelineStatus`
      | `deployment.deploymentPipelinesStatus`,
      5
    > & {
      __applicationErrorsType?: PublishDeploymentApplicationErrors;
    }
  >;
}

export function publishRcDeployment(
  httpClient: HttpClient
): PublishRcDeploymentSignature {
  return (deploymentId: string, options?: PublishRcDeploymentOptions) =>
    universalPublishRcDeployment(
      deploymentId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface PublishRcDeploymentSignature {
  /**
   * Publish an RC Deployment
   * @param - Id of the Deployment to publish
   */
  (deploymentId: string, options?: PublishRcDeploymentOptions): Promise<
    NonNullablePaths<
      PublishRCDeploymentResponse,
      | `deployment.editorRevision.siteRevision`
      | `deployment.rcPublish.rcLabel`
      | `deployment.rcPublish.useUniqueLabel`
      | `deployment._id`
      | `deployment.deploymentType`
      | `deployment.pipelines`
      | `deployment.pipelines.${number}.deploymentPipelineId`
      | `deployment.pipelines.${number}.pipelineStatus`
      | `deployment.deploymentPipelinesStatus`,
      5
    > & {
      __applicationErrorsType?: PublishRcDeploymentApplicationErrors;
    }
  >;
}

export function listDeployments(
  httpClient: HttpClient
): ListDeploymentsSignature {
  return () =>
    universalListDeployments(
      // @ts-ignore
      { httpClient }
    );
}

interface ListDeploymentsSignature {
  /**
   * List Deployments
   */
  (): Promise<
    NonNullablePaths<
      ListDeploymentsResponse,
      | `deployments`
      | `deployments.${number}.editorRevision.siteRevision`
      | `deployments.${number}.rcPublish.rcLabel`
      | `deployments.${number}.rcPublish.useUniqueLabel`
      | `deployments.${number}._id`
      | `deployments.${number}.deploymentType`
      | `deployments.${number}.deploymentPipelinesStatus`,
      5
    >
  >;
}

export function queryDeployments(
  httpClient: HttpClient
): QueryDeploymentsSignature {
  return () =>
    universalQueryDeployments(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryDeploymentsSignature {
  /**
   * Query Deployments
   */
  (): DeploymentsQueryBuilder;
}

export function reportDeploymentPipelineResult(
  httpClient: HttpClient
): ReportDeploymentPipelineResultSignature {
  return (
    deploymentId: string,
    deploymentPipelineId: string,
    options?: ReportDeploymentPipelineResultOptions
  ) =>
    universalReportDeploymentPipelineResult(
      deploymentId,
      deploymentPipelineId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ReportDeploymentPipelineResultSignature {
  /**
   * The pipeline provider has to send this event when its execution has ended.
   * @param - The deployment creation process id, should match the id that was passed in the invoke() call by the SPI Host
   * @param - The deployment pipeline implementer id as appears in the SPI Config
   */
  (
    deploymentId: string,
    deploymentPipelineId: string,
    options?: ReportDeploymentPipelineResultOptions
  ): Promise<
    void & {
      __applicationErrorsType?: ReportDeploymentPipelineResultApplicationErrors;
    }
  >;
}

export function getDeploymentPipelineDescription(
  httpClient: HttpClient
): GetDeploymentPipelineDescriptionSignature {
  return (deploymentId: string, deploymentPipelinesIds: string[]) =>
    universalGetDeploymentPipelineDescription(
      deploymentId,
      deploymentPipelinesIds,
      // @ts-ignore
      { httpClient }
    );
}

interface GetDeploymentPipelineDescriptionSignature {
  /**
   * Get Deployment Pipeline Description
   * @param - The deployment id
   * @param - The deployment pipelines implementers ids as appears in the SPI Config to be called for getting descriptions.
   */
  (deploymentId: string, deploymentPipelinesIds: string[]): Promise<
    NonNullablePaths<
      GetDeploymentPipelineDescriptionResponse,
      | `pipelinesDescription`
      | `pipelinesDescription.${number}.deploymentPipelineId`,
      4
    > & {
      __applicationErrorsType?: GetDeploymentPipelineDescriptionApplicationErrors;
    }
  >;
}

export function updateExtendedFields(
  httpClient: HttpClient
): UpdateExtendedFieldsSignature {
  return (
    _id: string,
    namespace: string,
    options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
  ) =>
    universalUpdateExtendedFields(
      _id,
      namespace,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateExtendedFieldsSignature {
  /**
   * Updates extended fields of a Deployment without incrementing revision
   * @param - ID of the entity to update.
   * @param - Identifier for the app whose extended fields are being updated.
   */
  (
    _id: string,
    namespace: string,
    options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
  ): Promise<
    NonNullablePaths<
      UpdateExtendedFieldsResponse,
      | `deployment.editorRevision.siteRevision`
      | `deployment.rcPublish.rcLabel`
      | `deployment.rcPublish.useUniqueLabel`
      | `deployment._id`
      | `deployment.deploymentType`
      | `deployment.pipelines`
      | `deployment.pipelines.${number}.deploymentPipelineId`
      | `deployment.pipelines.${number}.pipelineStatus`
      | `deployment.deploymentPipelinesStatus`,
      5
    >
  >;
}

export {
  ActionEvent,
  CreateDeploymentOptions,
  CreateDeploymentOptionsBasedOnOneOf,
  CreateDeploymentOptionsPublishMethodOneOf,
  CreateDeploymentRequest,
  CreateDeploymentRequestBasedOnOneOf,
  CreateDeploymentRequestPublishMethodOneOf,
  CreateDeploymentResponse,
  CreationPublishTypeNone,
  CreationPublishTypeRC,
  CreationPublishTypeSite,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  Deployment,
  DeploymentAttribute,
  DeploymentAttributeAttributeOneOf,
  DeploymentCreationPublishTypeOneOf,
  DeploymentDescriptionOneOf,
  DeploymentPipelineUpdatedMessage,
  DeploymentPipelinesStatus,
  DeploymentType,
  DeploymentsQueryBuilder,
  DeploymentsQueryResult,
  DomainEvent,
  DomainEventBodyOneOf,
  EditorRevision,
  EditorSession,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  ExtendedFields,
  GetDeploymentPipelineDescriptionRequest,
  GetDeploymentPipelineDescriptionResponse,
  GetDeploymentRequest,
  GetDeploymentResponse,
  GridAppIdAttribute,
  IdentificationData,
  IdentificationDataIdOneOf,
  LatestFromBranch,
  ListDeploymentsRequest,
  ListDeploymentsResponse,
  MessageEnvelope,
  PipelineDescription,
  PipelineResult,
  PipelineStatus,
  PublishDeploymentOptions,
  PublishDeploymentRequest,
  PublishDeploymentResponse,
  PublishRCDeploymentRequest,
  PublishRCDeploymentResponse,
  PublishRcDeploymentOptions,
  PublishSiteRCMethod,
  PublishType,
  QueryDeploymentsRequest,
  QueryDeploymentsResponse,
  RCLabel,
  ReportDeploymentPipelineResultOptions,
  ReportDeploymentPipelineResultRequest,
  ReportDeploymentPipelineResultResponse,
  RestoreInfo,
  SessionInfo,
  SortOrder,
  Sorting,
  TaskDescription,
  TaskResult,
  TaskStatus,
  UpdateExtendedFieldsOptions,
  UpdateExtendedFieldsRequest,
  UpdateExtendedFieldsResponse,
  WebhookIdentityType,
} from './document-management-editor-deployments-v1-deployment-deployments.universal.js';
